import type { Map as MaplibreMap } from 'maplibre-gl';

/** Unit system for {@link computeScaleBarSnapshot} / host scale bar. */
export type ScaleBarHostUnit = 'metric' | 'imperial' | 'nautical';

/** Numeric + label state for rendering a scale bar in the host app. */
export type ScaleBarSnapshot = {
  distance: number;
  unit: string;
  maxWidthPx: number;
  barLengthPx: number;
  ratio: number;
  labels: {
    leftOuter: string;
    leftMid: string;
    center: string;
    rightInner: string;
    end: string;
  };
  segmentWidthsPx: [number, number, number, number];
};

/** Subscribe to scale bar updates and render UI in the host (Vue/React). */
export type ScaleBarHostOptions = {
  onUpdate: (snapshot: ScaleBarSnapshot) => void;
  maxWidth?: number;
  unit?: ScaleBarHostUnit;
};

export type ComputeScaleBarOptions = {
  maxWidth?: number;
  unit?: ScaleBarHostUnit;
};

function getUI(map: MaplibreMap, key: string, fallback: string): string {
  const g = map as any;
  return typeof g._getUIString === 'function' ? g._getUIString(key) : fallback;
}

function getDecimalRoundNum(d: number): number {
  const multiplier = Math.pow(10, Math.ceil(-Math.log(d) / Math.LN10));
  return Math.round(d * multiplier) / multiplier;
}

function getRoundNum(num: number): number {
  const pow10 = Math.pow(10, `${Math.floor(num)}`.length - 1);
  let d = num / pow10;

  d =
    d >= 10
      ? 10
      : d >= 5
        ? 5
        : d >= 3
          ? 3
          : d >= 2
            ? 2
            : d >= 1
              ? 1
              : getDecimalRoundNum(d);

  return pow10 * d;
}

function buildSnapshot(maxWidth: number, maxDistance: number, unit: string): ScaleBarSnapshot {
  let distance = getRoundNum(maxDistance);
  if (!distance) distance = 5;
  const ratio = distance / maxDistance;
  const length = maxWidth * ratio;

  return {
    distance,
    unit,
    maxWidthPx: maxWidth,
    barLengthPx: length,
    ratio,
    labels: {
      leftOuter: distance.toLocaleString(),
      leftMid: (distance / 2).toLocaleString(),
      center: '0',
      rightInner: distance.toLocaleString(),
      end: `${(distance * 2).toLocaleString()} ${unit}`,
    },
    segmentWidthsPx: [length / 2, length / 2, length, length],
  };
}

/**
 * Pure scale-bar state from the current map view (no DOM).
 * Use from Vue/React via {@link Map.enableHostScaleBar} or call directly in the host app.
 */
export function computeScaleBarSnapshot(
  map: MaplibreMap,
  options?: ComputeScaleBarOptions
): ScaleBarSnapshot {
  const optWidth = options?.maxWidth ?? 100;
  const unitOpt = options?.unit ?? 'metric';
  const y = map._container.clientHeight / 2;
  const x = map._container.clientWidth / 2;
  const left = map.unproject([x - optWidth / 2, y]);
  const right = map.unproject([x + optWidth / 2, y]);

  const globeWidth = Math.round(map.project(right).x - map.project(left).x);
  const maxWidth = Math.min(optWidth, globeWidth, map._container.clientWidth);

  const maxMeters = left.distanceTo(right);

  if (unitOpt === 'imperial') {
    const maxFeet = 3.2808 * maxMeters;
    if (maxFeet > 5280) {
      const maxMiles = maxFeet / 5280;
      return buildSnapshot(maxWidth, maxMiles, getUI(map, 'ScaleControl.Miles', 'mi'));
    }
    return buildSnapshot(maxWidth, maxFeet, getUI(map, 'ScaleControl.Feet', 'ft'));
  }
  if (unitOpt === 'nautical') {
    const maxNauticals = maxMeters / 1852;
    return buildSnapshot(maxWidth, maxNauticals, getUI(map, 'ScaleControl.NauticalMiles', 'nm'));
  }
  if (maxMeters >= 1000) {
    return buildSnapshot(maxWidth, maxMeters / 1000, getUI(map, 'ScaleControl.Kilometers', 'km'));
  }
  if (maxMeters >= 1) {
    return buildSnapshot(maxWidth, maxMeters, getUI(map, 'ScaleControl.Meters', 'm'));
  }
  return buildSnapshot(maxWidth, maxMeters * 100, 'cm');
}

/** Apply a snapshot to the SDK scale bar DOM under `container` (scoped queries). */
export function applyScaleBarSnapshotToContainer(
  container: HTMLElement,
  snapshot: ScaleBarSnapshot
): void {
  const q = (name: string) =>
    container.querySelector(`[data-scalebar="${name}"]`) as HTMLElement | null;

  const setLabel = (name: string, text: string, widthPx: number) => {
    const el = q(name);
    if (el) {
      el.textContent = text;
      el.style.width = `${widthPx}px`;
    }
  };

  const [w0, w1, w2, w3] = snapshot.segmentWidthsPx;
  setLabel('left2', snapshot.labels.leftOuter, w0);
  setLabel('left1', snapshot.labels.leftMid, w1);
  setLabel('zero', snapshot.labels.center, w2);
  setLabel('right', snapshot.labels.rightInner, w3);
  setLabel('end', snapshot.labels.end, w3);

  const segments = container.querySelectorAll('.geobox-gl-scalebar-segment');
  let i = 0;
  segments.forEach((seg) => {
    i += 1;
    const el = seg as HTMLElement;
    if (i === 1 || i === 2) {
      el.style.width = `${snapshot.barLengthPx / 2}px`;
    } else {
      el.style.width = `${snapshot.barLengthPx}px`;
    }
  });
}
